<template>
  <div class="clausulas-comerciais-form-wrapper" v-bind="$attrs">
    <FormularioCadastro
      module-name="Cláusulas Comerciais"
      :is-loading="isLoading"
      :is-editing="isEditing"
      :entity-info="entityInfo"
      :accordion-state="accordionState"
      @submit="submitFormulario"
      @submit-continuar="salvarEContinuar"
      @cancelar="handleCancel"
      @show-notification="(p) => emit('show-notification', p)"
    >
      <template #acoes-header v-if="isEditing">
        <button
          type="button"
          class="btn-icon-minimalista me-3"
          @click="mostrarAuditoria = !mostrarAuditoria"
          title="Ver Histórico de Alterações"
        >
          <i class="bi bi-clock-history"></i>
        </button>
      </template>

      <!-- FRAME 1: DADOS BÁSICOS -->
      <div class="card mb-3 border-primary-subtle">
        <div class="card-header bg-primary-subtle d-flex justify-content-between align-items-center cursor-pointer" @click="toggleAccordion('dadosBasicos')">
          <h5 class="mb-0 text-primary-emphasis">
            <i class="bi bi-file-earmark-text me-2"></i>Dados Básicos
          </h5>
          <i class="bi" :class="accordionState.dadosBasicos ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        </div>
        <div v-show="accordionState.dadosBasicos" class="card-body border-top border-primary-subtle">
          <div class="row g-3">
            <div class="col-md-6">
              <CampoInput
                id="titulo"
                label="Título"
                v-model="item.titulo"
                required
                maxlength="200"
              />
            </div>
            <div class="col-md-3">
              <CampoInput
                id="ordemExibicao"
                label="Ordem de Exibição"
                v-model="item.ordemExibicao"
                type="decimal"
              />
            </div>
            <div class="col-md-3">
              <CampoSelect
                id="isDefinitiva"
                label="Cláusula Definitiva?"
                v-model="item.isDefinitiva"
                option-value="id"
                option-label="label"
                :options="[
                  { id: true, label: 'Sim' },
                  { id: false, label: 'Não' }
                ]"
              />
            </div>
            <div class="col-12">
              <CampoInput
                id="texto"
                label="Texto da Cláusula"
                v-model="item.texto"
                type="textarea"
                required
              />
            </div>
            <div class="col-md-4">
              <CampoSelect
                id="isFixa"
                label="Cláusula Fixa?"
                v-model="item.isFixa"
                option-value="id"
                option-label="label"
                :options="[
                  { id: true, label: 'Sim' },
                  { id: false, label: 'Não' }
                ]"
              />
            </div>
            <div class="col-md-4">
              <CampoBuscaLista
                id="lstAplicacaoClausula"
                label="Aplicação (Módulo)"
                v-model="item.lstAplicacaoClausula"
                required
                tabela="ClausulasComerciais"
                placeholder="Selecione a aplicação..."
              />
            </div>
            <div class="col-md-4">
              <CampoSelect
                id="lstStatusClausula"
                label="Status"
                v-model="item.lstStatusClausula"
                :options="listas.LstStatusClausula || []"
                option-value="idListaClienteItem"
                option-label="textoLista"
                placeholder="Selecione o status..."
              />
            </div>
          </div>
        </div>
      </div>

      <!-- AUDITORIA -->
      <AuditLogViewer
        v-if="mostrarAuditoria"
        tabela="ClausulasComerciais"
        :chave-primaria="item.idClausulasComerciais"
        :titulo-registro="item.titulo"
        contexto="tenant"
        @fechar="mostrarAuditoria = false"
      />
    </FormularioCadastro>
  </div>
</template>

<script setup>
import { reactive, onMounted, ref, computed } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { useMetadataListasStore } from '@/stores/metadataListas'
import api from '@/services/api'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  },
  modeloVazio: {
    type: Object,
    default: () => ({
      idClausulasComerciais: 0,
      titulo: '',
      texto: '',
      ordemExibicao: null,
      isDefinitiva: false,
      isFixa: false,
      lstStatusClausula: '',
      lstAplicacaoClausula: ''
    })
  }
})

const emit = defineEmits([
  'itemAtualizado',
  'cancelar',
  'show-notification',
  'submit-continuar'
])

const metadataStore = useMetadataListasStore()
const mostrarAuditoria = ref(false)

const listas = reactive({})

const accordionState = reactive({
  dadosBasicos: true
})

const {
  item,
  isLoading,
  isEditing,
  errors,
  submitForm,
  handleCancel
} = useFormulario(props, emit)

const entityInfo = computed(() => ({
  id: item.value.idClausulasComerciais,
  name: item.value.titulo || 'Cláusula',
  nameLabel: 'Título'
}))

const toggleAccordion = (key) => {
  accordionState[key] = !accordionState[key]
}

const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['ClausulasComerciais'])
    Object.assign(listas, result)
  } catch (e) {
    console.error('Erro ao carregar listas auxiliares via metadados:', e)
    emit('show-notification', { message: 'Erro ao carregar listas auxiliares via metadados.', type: 'error' })
  }
}

onMounted(() => {
  carregarListas()
})

const validarRegrasNegocio = () => {
  const validationErrors = {}

  if (!item.value.titulo || item.value.titulo.trim() === '') {
    validationErrors.titulo = 'O campo "Título" é de preenchimento obrigatório.'
  }

  if (!item.value.texto || item.value.texto.trim() === '') {
    validationErrors.texto = 'O campo "Texto da Cláusula" é de preenchimento obrigatório.'
  }

  if (!item.value.lstAplicacaoClausula) {
    validationErrors.lstAplicacaoClausula = 'O campo "Aplicação da Cláusula" é de preenchimento obrigatório.'
  }

  if (Object.keys(validationErrors).length > 0) {
    errors.value = validationErrors
    const primeiraMensagem = Object.values(validationErrors)[0]
    emit('show-notification', { message: primeiraMensagem, type: 'warning' })
    return false
  }

  return true
}

const submitFormulario = async () => {
  if (!validarRegrasNegocio()) return

  const payload = {
    ...item.value
  }

  const success = await submitForm('ClausulasComerciais', 'idClausulasComerciais', payload)

  if (success) {
    emit('show-notification', { message: 'Cláusula comercial salva com sucesso!', type: 'success' })
  }
}

const salvarEContinuar = async () => {
  if (!validarRegrasNegocio()) return

  const payload = {
    ...item.value
  }

  try {
    const res = item.value.idClausulasComerciais
      ? await api.put(`/ClausulasComerciais/${item.value.idClausulasComerciais}`, payload)
      : await api.post('/ClausulasComerciais', payload)

    if (res?.data) {
      Object.assign(item.value, res.data)
    }
    emit('show-notification', { message: 'Alterações salvas com sucesso!', type: 'success' })
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar alterações.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
